import React, { memo, useCallback, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import _ from "lodash";
import { Button } from "@toddle-edu/ds-web";
import { AddCircleOutlined } from "@toddle-edu/ds-icons";
import { DataGrid } from "@toddle-edu/ds-data-grid";
import { useI18n, useContainerWidth } from "Hooks";
import {
  ASSOCIATION_ENTITY_COLLECTION_KEYS,
  CUSTOM_GRID_HEADER_HEIGHT,
  CUSTOM_GRID_REORDER_COL_ID,
  CUSTOM_GRID_ROW_HEIGHT,
  CUSTOM_GRID_TEST_ID,
  TOOLTIP_DELAY_TIME,
} from "../modules/constants";
import { resolveFlexHeaders } from "TranscriptsV3/modules/utils";
import classes from "./CustomGrid.scss";
import { getCustomGridHeaders, getCustomGridData } from "./CustomGridUtils";

const CustomGrid = ({ config }) => {
  const containerRef = useRef(null);
  const containerWidth = useContainerWidth({ containerRef });
  const { t } = useI18n(["common", "transcript"]);
  const [focusCell, setFocusCell] = useState({
    rowId: null,
    colId: null,
  });

  const {
    associationEntityCollectionKey,
    deleteTooltipText,
    emptyRow,
    emptyRowIncludeInGpa,
    errorText = t("transcript:enter_this_field_to_save_changes"),
    includeInGpaLabel,
    items,
    nameLabel = t("common:name"),
    onAdd,
    onDelete,
    onIncludeInGpaChange,
    onRowsReorder,
    onValueChange,
    placeholder,
    usedInTranscriptsLabel = t("transcript:used_in_transcripts_for"),
    valueKey = "name",
  } = config;

  const shouldShowIncludeInGpa = Boolean(includeInGpaLabel);
  const rows = useMemo(() => items || [], [items]);

  const handleRowsReorder = useCallback(
    (startIdx, endIdx) => {
      const rowId = _.get(rows, [startIdx, "id"]);
      if (!rowId || endIdx < 0 || endIdx >= rows.length) return;

      onRowsReorder?.(startIdx, endIdx);

      setFocusCell({
        rowId,
        colId: CUSTOM_GRID_REORDER_COL_ID,
      });

      setTimeout(() => {
        setFocusCell({
          rowId: null,
          colId: null,
        });
      });
    },
    [onRowsReorder, rows]
  );

  const headers = useMemo(() => {
    const rawHeaders = getCustomGridHeaders({
      includeInGpaLabel,
      nameLabel,
      shouldShowIncludeInGpa,
      usedInTranscriptsLabel,
    });
    return resolveFlexHeaders({ headers: rawHeaders, containerWidth });
  }, [
    includeInGpaLabel,
    nameLabel,
    shouldShowIncludeInGpa,
    usedInTranscriptsLabel,
    containerWidth,
  ]);

  const data = useMemo(
    () =>
      getCustomGridData({
        associationEntityCollectionKey,
        emptyRow,
        emptyRowIncludeInGpa,
        errorText,
        deleteTooltipText,
        onDelete,
        onRowsReorder: handleRowsReorder,
        placeholder,
        rows,
        shouldShowIncludeInGpa,
        t,
        valueKey,
      }),
    [
      associationEntityCollectionKey,
      emptyRow,
      emptyRowIncludeInGpa,
      errorText,
      deleteTooltipText,
      onDelete,
      handleRowsReorder,
      placeholder,
      rows,
      shouldShowIncludeInGpa,
      t,
      valueKey,
    ]
  );

  const handleCellEdit = useCallback(
    edits => {
      _.forEach(edits, ({ cellCoods, newValue }) => {
        const { row, colId: previousColId } = cellCoods || {};
        const colId = newValue?.colId || previousColId;
        const value = _.has(newValue, "value") ? newValue.value : newValue;

        const isNameCol = colId === "name";
        const isGpaCol = colId === "gpa";
        if (row >= rows.length) {
          if (isNameCol) {
            const event = { target: { value } };
            emptyRow?.onChange?.(event);
          }
          return;
        }

        const rowItem = _.nth(rows, row);
        if (!rowItem) return;

        if (isNameCol) {
          onValueChange?.(rowItem.id, value);
        } else if (isGpaCol) {
          onIncludeInGpaChange?.(rowItem.id, value);
        }
      });
    },
    [emptyRow, onIncludeInGpaChange, onValueChange, rows]
  );

  return (
    <>
      <div className={classes.customGridContainer}>
        <div ref={containerRef} className={classes.gridWrapper}>
          <DataGrid
            headers={headers}
            data={data}
            rowHeight={CUSTOM_GRID_ROW_HEIGHT}
            headerHeight={CUSTOM_GRID_HEADER_HEIGHT}
            dataGridHeight="100%"
            dataGridWidth="100%"
            rowMarkers="none"
            columnsResizable={false}
            blurSelectionOnOutsideClick
            onCellEdit={handleCellEdit}
            focusCell={focusCell}
            tooltipDelayTime={TOOLTIP_DELAY_TIME}
          />
        </div>
      </div>
      <Button
        type="inline"
        variant="progressive"
        icon={<AddCircleOutlined />}
        onClick={onAdd}
        className={classes.customGridAddButton}
        testId={`${CUSTOM_GRID_TEST_ID}-add`}
      >
        {t("common:create_new")}
      </Button>
    </>
  );
};

CustomGrid.displayName = "CustomGrid";

CustomGrid.propTypes = {
  config: PropTypes.shape({
    associationEntityCollectionKey: PropTypes.oneOf(
      Object.values(ASSOCIATION_ENTITY_COLLECTION_KEYS)
    ),
    errorText: PropTypes.string,
  }).isRequired,
};

export default memo(CustomGrid);
